import React, { useState, useEffect, useMemo } from 'react';

import { getOrders } from '@/api/orders';
import { isDesktopOffline } from '@/api/desktopSync';
import { buildOfflineRangeMessage } from '@/utils/offlineCacheRange';

/**
 * OfflineInfoBar — the shared amber bar shown flush under the nav on every
 * read-only offline tab, so its wording and styling stay identical everywhere.
 *
 * Reads the whole local cache once (unfiltered, so it's independent of any tab's
 * active filters) to state the earliest date its orders reach. Renders nothing
 * when online. `flex-shrink-0` keeps it full-height inside flex-column layouts
 * (Design, Deliver) as well as normal block flow (All Orders).
 */
export default function OfflineInfoBar() {
  const offline = isDesktopOffline();
  const [cacheRange, setCacheRange] = useState(null);

  useEffect(() => {
    if (!offline) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const all = await getOrders({ window: 'all', limit: 1000 });
        if (cancelled) return;
        let earliest = null;
        let undated = 0;
        for (const o of all) {
          const d = o.delivery_date;
          if (!d) { undated += 1; continue; }
          if (!earliest || d < earliest) earliest = d;   // YYYY-MM-DD sorts lexically
        }
        setCacheRange({ earliest, undated });
      } catch {
        // Leave the bar generic on failure.
      }
    })();
    return () => { cancelled = true; };
  }, [offline]);

  const message = useMemo(() => buildOfflineRangeMessage(cacheRange), [cacheRange]);

  if (!offline) return null;

  return (
    <div
      className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium flex-shrink-0"
      data-testid="offline-info-bar"
    >
      {message}
    </div>
  );
}
